<script>
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import medicalServicesService from "@/services/medical-services";
import medicalServicesOverrideService from "@/services/medical-services-override";
import ModalServiceOverride from "./ModalServiceOverride.vue";
import { capitalize } from "lodash";

export default {
  name: "DoctorServices",
  components: {
    BaseSubNav,
    ModalServiceOverride,
  },
  data() {
    return {
      doctor: null,
      allMedicalServices: [],
      doctorOverrides: [],
      combinedServices: [],
      searchQuery: "",
      isLoading: false,
      currentPage: 1,
      pageSize: 20,
      totalPages: 0,
      totalItems: 0,
      paginationInfo: null,
      searchTimeout: null,
      showModal: false,
      selectedService: null,
    };
  },
  computed: {
    filteredServices() {
      return this.combinedServices;
    },

    hasAnyOverrides() {
      return this.combinedServices && this.combinedServices.length > 0;
    },
  },
  watch: {
    searchQuery: {
      handler() {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.currentPage = 1;
          this.loadAllData();
        }, 500);
      },
    },
  },
  async created() {
    const doctorFromQuery = this.$route.query?.doctor;

    if (doctorFromQuery) {
      try {
        this.doctor = JSON.parse(doctorFromQuery);
        await this.loadAllData();
      } catch (error) {
        console.error("Error al parsear información del doctor:", error);
        this.$router.push({ name: "contracts" });
      }
    } else {
      console.warn(
        "No se encontró información del doctor. Redirigiendo a contratos."
      );
      this.$router.push({ name: "contracts" });
    }
  },
  methods: {
    capitalize,

    capitalizeName(name) {
      if (!name) return "";
      return name
        .split(" ")
        .map((word) => capitalize(word))
        .join(" ");
    },
    async loadAllData() {
      this.isLoading = true;
      try {
        if (this.searchQuery.trim()) {
          const searchQuery = {
            name: { $regex: this.searchQuery, $options: "i" },
          };

          const paginatedServices = await medicalServicesService.getPaginated(
            this.currentPage,
            this.pageSize,
            searchQuery
          );

          const overrides = await medicalServicesOverrideService
            .getByDoctor(this.doctor._id)
            .catch((error) => {
              console.error("Error fetching overrides:", error);
              if (error.response && error.response.status === 500) {
                console.warn(
                  "Backend error 500 for overrides, returning empty array"
                );
                return [];
              }
              throw error;
            });

          this.paginationInfo = paginatedServices;
          this.allMedicalServices = paginatedServices.docs || paginatedServices;
          this.totalPages = paginatedServices.totalPages || 0;
          this.totalItems =
            paginatedServices.totalDocs || paginatedServices.total || 0;

          this.doctorOverrides = overrides;
          this.combineServices();
        } else {
          const overrides = await medicalServicesOverrideService
            .getByDoctor(this.doctor._id)
            .catch((error) => {
              console.error("Error fetching overrides:", error);
              if (error.response && error.response.status === 500) {
                console.warn(
                  "Backend error 500 for overrides, returning empty array"
                );
                return [];
              }
              throw error;
            });

          this.combinedServices = overrides.map((override) => ({
            _id: override.medicalService._id,
            code: override.medicalService.code,
            name: override.medicalService.name,
            description: override.medicalService.description,
            price: override.medicalService.price,
            hasOverride: true,
            overrideId: override._id,
            priceOverride: override.priceOverride,
            enabled: override.enabled,
            originalPrice: override.medicalService.price,
          }));

          this.paginationInfo = null;
          this.totalPages = 0;
          this.totalItems = overrides.length;
        }
      } catch (error) {
        console.error("Error al cargar datos:", error);
        this.allMedicalServices = [];
        this.doctorOverrides = [];
        this.combinedServices = [];
        this.paginationInfo = null;
      } finally {
        this.isLoading = false;
      }
    },

    combineServices() {
      this.combinedServices = this.allMedicalServices.map((service) => {
        const override = this.doctorOverrides.find(
          (o) => o.medicalService._id === service._id
        );

        return {
          ...service,
          hasOverride: !!override,
          overrideId: override?._id,
          priceOverride: override?.priceOverride || null,
          enabled: override?.enabled ?? false,
          originalPrice: service.price,
        };
      });
    },

    goBack() {
      this.$router.push({ name: "contracts" });
    },

    openEditModal(service) {
      this.selectedService = service;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.selectedService = null;
    },

    async onServiceSaved() {
      await this.loadAllData();
    },

    async goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        await this.loadAllData();
      }
    },

    async nextPage() {
      if (this.currentPage < this.totalPages) {
        await this.goToPage(this.currentPage + 1);
      }
    },

    async prevPage() {
      if (this.currentPage > 1) {
        await this.goToPage(this.currentPage - 1);
      }
    },

    async changePageSize(newSize) {
      this.pageSize = newSize;
      this.currentPage = 1;
      await this.loadAllData();
    },

    getVisiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const pages = [];

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);

        if (current > 4) {
          pages.push("...");
        }

        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);

        for (let i = start; i <= end; i++) {
          if (i !== 1 && i !== total) {
            pages.push(i);
          }
        }

        if (current < total - 3) {
          pages.push("...");
        }

        if (total > 1) {
          pages.push(total);
        }
      }

      return pages;
    },
  },
};
</script>

<template lang="pug">
  div
    BaseSubNav
      template(#title)
        | Prestaciones del doctor {{capitalizeName(doctor.firstName)}} {{capitalizeName(doctor.lastName)}}
      template(#actions)
        .subnav-actions
          .search-container
            input.search-input(
              v-model="searchQuery"
              placeholder="Buscar prestaciones..."
              type="text"
            )
            micon.search-icon(name="search")
    .doctor-services-container      
      .loading(v-if="isLoading")
        p Cargando prestaciones...
      
      .box.box--with-subnav(v-if="!isLoading && filteredServices.length > 0")
        .box__content--stretch
          table
            thead
              tr
                th Cod. Prestación
                th Prestación
                th Precio personalizado
                th Estado
                th Acciones
            tbody
              tr(v-for="service in filteredServices" :key="service._id" :class="{ 'has-override': service.hasOverride }")
                td.service-code {{ service.code }}
                td.service-name {{ service.name }}
                  p(v-if="service.description") {{ service.description }}
                
                td.override-price
                  span(v-if="service.hasOverride") $ {{ service.priceOverride }}
                  span(v-else) Sin precio asignado
                
                td.status
                  span(v-if="service.hasOverride") {{ service.enabled ? 'Activo' : 'Inactivo' }}
                  span(v-else) Sin configurar
                
                td.actions
                  .action-buttons
                    button.button.button--small(
                      :class="{ 'button--primary': service.hasOverride, 'button--secondary': !service.hasOverride }"
                      @click="openEditModal(service)"
                    )
                      micon(name="edit")
                      span {{ service.hasOverride ? 'Editar' : 'Configurar' }}
      
      .empty-state(v-if="!isLoading && filteredServices.length === 0")
        p(v-if="!searchQuery && !hasAnyOverrides") Todavía no se ha configurado ninguna prestación para este doctor
        p(v-else-if="!searchQuery && hasAnyOverrides") No hay prestaciones configuradas para mostrar
        p(v-else) No se encontraron prestaciones que coincidan con "{{ searchQuery }}".
      
      .pagination-controls(v-if="!isLoading && totalPages > 1 && searchQuery.trim()")
        .pagination-info
          span(v-if="!searchQuery") Mostrando {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, totalItems) }} de {{ totalItems }} prestaciones
          span(v-else) Mostrando {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, totalItems) }} de {{ totalItems }} resultados para "{{ searchQuery }}"
          
        .pagination-buttons
          button.button.button--secondary(
            @click="prevPage"
            :disabled="currentPage === 1"
          )
            micon(name="chevron-left")
            span Anterior
          
          .page-numbers
            button.page-btn(
              v-for="page in getVisiblePages()"
              :key="page"
              :class="{ 'active': page === currentPage, 'disabled': page === '...' }"
              @click="page !== '...' && goToPage(page)"
              :disabled="page === '...'"
            ) {{ page }}
          
          button.button.button--secondary(
            @click="nextPage"
            :disabled="currentPage === totalPages"
          )
            span Siguiente
            micon(name="chevron-right")
          
        .pagination-size
          label Tamaño de página:
          select.page-size-select(v-model="pageSize" @change="changePageSize(parseInt($event.target.value))")
            option(value="10") 10
            option(value="20") 20
            option(value="50") 50
            option(value="100") 100

    ModalServiceOverride(
      :show-dialog="showModal"
      :selected-service="selectedService"
      :doctor="doctor"
      @close="closeModal"
      @saved="onServiceSaved"
    )
</template>

<style lang="scss" scoped>
.subnav-actions {
  display: flex;
  align-items: center;
  gap: 1rem;

  .search-container {
    position: relative;
    display: flex;
    align-items: center;

    .search-input {
      padding: 0.5rem 2.5rem 0.5rem 1rem;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 0.9rem;
      width: 250px;
      transition: border-color 0.2s ease;

      &:focus {
        border-color: #040055;
        outline: none;
      }

      &::placeholder {
        color: #999;
      }
    }

    .search-icon {
      position: absolute;
      right: 0.75rem;
      color: #666;
      font-size: 1rem;
      pointer-events: none;
    }
  }
}

.doctor-services-container {
  .box {
    .box__header {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      padding: 1rem;

      h3 {
        color: #040055;
        margin: 0;
        font-size: 1.3rem;
      }
    }

    table {
      tbody {
        tr {
          &.has-override {
            background: #f0f8ff;
            border-left: 4px solid #040055;
          }

          td {
            &.service-name {
              p {
                color: #666;
                font-size: 0.9rem;
                margin: 0.25rem 0 0 0;
              }
            }

            &.original-price {
              .price {
                font-weight: 600;
                color: #333;
              }
            }

            &.override-price {
              input {
                width: 120px;
                padding: 0.5rem;
                border: 1px solid #ddd;
                border-radius: 4px;
                font-size: 0.9rem;

                &:focus {
                  border-color: #040055;
                  outline: none;
                }
              }

              span {
                color: #999;
                font-style: italic;
              }
            }

            &.status {
              display: flex;
              align-items: center;
              gap: 0.5rem;

              .status-text {
                font-size: 0.9rem;
                color: #666;
              }
            }

            &.actions {
              .action-buttons {
                display: flex;
                gap: 0.5rem;
              }
            }
          }
        }
      }
    }
  }

  .loading,
  .empty-state {
    text-align: center;
    padding: 2rem;
    color: #666;
    font-style: italic;
  }

  .pagination-controls {
    margin-top: 2rem;
    padding: 1.5rem;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .pagination-info {
      text-align: center;
      color: #666;
      font-size: 0.9rem;
    }

    .pagination-buttons {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 0.5rem;

      .page-numbers {
        display: flex;
        gap: 0.25rem;
        margin: 0 1rem;

        .page-btn {
          padding: 0.5rem 0.75rem;
          border: 1px solid #ddd;
          background: white;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.9rem;
          min-width: 40px;

          &:hover:not(.disabled) {
            background: #f8f9fa;
            border-color: #040055;
          }

          &.active {
            background: #040055;
            color: white;
            border-color: #040055;
          }

          &.disabled {
            cursor: not-allowed;
            opacity: 0.5;
          }
        }
      }
    }

    .pagination-size {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.9rem;
      color: #666;

      .page-size-select {
        padding: 0.25rem 0.5rem;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: white;
        cursor: pointer;

        &:focus {
          border-color: #040055;
          outline: none;
        }
      }
    }
  }
}
</style>
